import {
    ActionsDispatcher,
    ActionsImmediateDispatcher,
} from 'store/ActionsDispatcher';

import {
    StreamRequestInfo,
    WidgetCustomManagedOptions,
} from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManager';
import { WidgetStreamByRequestUidStreamManagerBase } from 'widgets/streaming/widgetStreamManager/WidgetStreamByRequestUidStreamManagerBase';
import { StreamError } from 'streamsState/store/streamStatuses/StreamErrorState';
import orderBy from 'lodash/orderBy';

const RetainStaleResponsesQty = 10;

export abstract class WidgetRequestResponseByUidManagerBase extends WidgetStreamByRequestUidStreamManagerBase {
    private latestRequestTimes: { [streamKey: string]: Date } = {};

    protected constructor(
        logSource: string,
        uuid: string,
        options: WidgetCustomManagedOptions
    ) {
        super(logSource, uuid, options);
    }

    protected abstract clearRequestData(
        dispatcher: ActionsDispatcher,
        key: string
    ): void;
    protected abstract callApi(
        dispatcher: ActionsDispatcher,
        payload: unknown
    ): Promise<unknown>;
    protected abstract handleMessage(
        dispatcher: ActionsDispatcher,
        key: string,
        request: unknown,
        response: unknown
    ): void;
    protected abstract handleError(
        dispatcher: ActionsDispatcher,
        key: string,
        request: unknown,
        error: unknown
    ): void;
    protected abstract removeRequestData(
        dispatcher: ActionsDispatcher,
        key: string
    ): void;

    public resetState() {
        super.resetState();
        this.latestRequestTimes = {};
    }

    protected async startStream(
        requestInfo: StreamRequestInfo<unknown>,
        dispatcher: ActionsDispatcher,
        reason: string
    ) {
        const { streamKey, payload } = requestInfo;

        this.log('Starting request', reason, { streamKey, payload });

        if (this.latestRequestTimes[streamKey]) {
            if (
                this.latestRequestTimes[streamKey].getDay() ===
                new Date().getDay()
            ) {
                // Currently we only use this for SymbolInfo and cache responses per day
                // Can extract a policy if usage increases
                this.log(
                    `Not requesting: already have requested data for ${streamKey} today`
                );
                return;
            }
        }

        this.latestRequestTimes[streamKey] = new Date();

        this.clearRequestData(dispatcher, streamKey);

        this.callApi(dispatcher, payload)
            .then((response: unknown) => {
                this.handleMessage(
                    new ActionsImmediateDispatcher(this.store),
                    streamKey,
                    payload,
                    response
                );
            })
            .catch((err: StreamError) => {
                this.handleError(
                    new ActionsImmediateDispatcher(this.store),
                    streamKey,
                    payload,
                    err
                );
            });
    }

    protected stopStream(
        _streamKey: string,
        _dispatcher: ActionsDispatcher,
        _reason: string
    ) {
        // Not applicable for RPC
    }

    protected override clearAndRemoveStaleStreamData(
        dispatcher: ActionsDispatcher,
        adding: string[],
        removing: string[],
        next: string[]
    ): string[] {
        const allRequestStreamKeys: string[] = Object.keys(
            this.latestRequestTimes
        );
        const staleRequestStreamKeys = allRequestStreamKeys.filter(
            (s) => next.indexOf(s) === -1
        );

        const qtyToRemove =
            staleRequestStreamKeys.length - RetainStaleResponsesQty;

        if (qtyToRemove <= 0) {
            return [];
        }

        const stateRequests = staleRequestStreamKeys.map((s) => ({
            streamKey: s,
            latestRequestTime: this.latestRequestTimes[s],
        }));
        const eldestFirst = orderBy(
            stateRequests,
            ['latestRequestTimes'],
            ['asc']
        );
        const toRemove = eldestFirst.slice(0, qtyToRemove);

        for (const r of toRemove) {
            this.removeRequestData(dispatcher, r.streamKey);
            delete this.latestRequestTimes[r.streamKey];
        }

        return toRemove.map((r) => r.streamKey);
    }

    public clearResponse(streamKey: string) {
        this.logger.debug('Clearing response for ' + streamKey, {
            latestRequestTimes: Object.keys(this.latestRequestTimes),
        });
        super.clearResponse(streamKey);
        this.removeRequestData(
            new ActionsImmediateDispatcher(this.store),
            streamKey
        );
        delete this.latestRequestTimes[streamKey];
        this.streamKeys = this.streamKeys.filter((k) => k !== streamKey);
    }
}
